import { instantOf } from '@moojo/duration'
import type { EndpointDeclaration, ServiceDeclaration } from '@moojo/endpoints'
import type { Logger } from '@moojo/logger'
import { errorLike, shouldNeverHappen } from '@moojo/misc-constructs'
import type { IncomingPayload, ResponseFormat } from '@moojo/serving-box-api'
import { ERROR_CODE_GENERIC, ERROR_CODE_PARSING, ResponseEnvelope } from '@moojo/serving-box-api'
import { ZodError } from 'zod'

import { EndpointCallError } from './endpoint-call-error'
import type { HeaderRecord, ResponselessTransfer, SearchParamsRecord, Transfer } from './transfer'
import { TransferError } from './transfer-error'

// TODO: sort out typing in this facility (no implicit anys).

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type RequestOf<Service extends ServiceDeclaration<any>, Endpoint extends keyof Service> = ReturnType<
  Service[Endpoint]['reqParser']['parse']
>

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ResponseOf<Service extends ServiceDeclaration<any>, Endpoint extends keyof Service> = ReturnType<
  Service[Endpoint]['respParser']['parse']
>

type StringKeysOf<T> = keyof T extends infer K
  ? K extends string
    ? K extends `${number}`
      ? never
      : K
    : never
  : never

// TODO(imaman): can we replace the `any` with `unknown`?
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type EndpointOf<Service extends ServiceDeclaration<any>> = StringKeysOf<Service>

// TODO(imaman): make throw HttpStatusErrors

/**
 * Configuration options for ServiceClient instances.
 */
interface Options {
  /**
   * Headers that will be automatically included with every request made by this client.
   * These headers are merged with any headers passed to individual endpoint calls,
   * with call-specific headers taking precedence over default headers.
   *
   * @example
   * ```typescript
   * const client = new ServiceClient(logger, serviceDeclaration, transfer, {
   *   defaultHeaders: {
   *     'User-Agent': 'MyApp/1.0',
   *     'X-Custom-Header': 'value'
   *   }
   * });
   * ```
   */
  defaultHeaders?: HeaderRecord

  /**
   * The response format expected from the service.
   * - 'raw': Returns the raw response data without any envelope wrapper
   * - 'envelope': Wraps responses in a standardized envelope with status and error handling
   *
   * @default 'raw'
   *
   * @example
   * ```typescript
   * // Raw format - returns data directly
   * const client = new ServiceClient(logger, serviceDeclaration, transfer, {
   *   acceptsFormat: 'raw'
   * });
   *
   * // Envelope format - returns { status: 'success', data: ... } or { status: 'error', error: ... }
   * const client = new ServiceClient(logger, serviceDeclaration, transfer, {
   *   acceptsFormat: 'envelope'
   * });
   * ```
   */
  acceptsFormat?: ResponseFormat

  /**
   * API key that will be automatically sent as the 'x-api-key' header with every request.
   * This is commonly used for service-to-service authentication.
   *
   * @example
   * ```typescript
   * const client = new ServiceClient(logger, serviceDeclaration, transfer, {
   *   apiKey: 'sk-1234567890abcdef'
   * });
   * // Results in header: { 'x-api-key': 'sk-1234567890abcdef' }
   * ```
   */
  apiKey?: string

  /**
   * Bearer token that will be automatically sent as the 'Authorization' header with every request.
   * The token is automatically prefixed with 'Bearer ' when sent.
   * This is commonly used for user authentication with JWT tokens or similar.
   *
   * @example
   * ```typescript
   * const client = new ServiceClient(logger, serviceDeclaration, transfer, {
   *   bearerToken: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...'
   * });
   * // Results in header: { 'Authorization': 'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...' }
   * ```
   */
  bearerToken?: string
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export class ServiceClient<Service extends ServiceDeclaration<any>> {
  private readonly options: Omit<Required<Options>, 'apiKey' | 'bearerToken'>
  constructor(
    private readonly logger: Logger,
    private readonly serviceDeclaration: Service,
    private readonly transfer: Transfer,
    options: ResponseFormat | Options = {},
  ) {
    options = typeof options === 'string' ? { acceptsFormat: options } : options
    this.options = {
      acceptsFormat: 'raw',
      ...options,
      defaultHeaders: {
        ...options.defaultHeaders,
        ...(options.apiKey ? { 'x-api-key': options.apiKey } : {}),
        ...(options.bearerToken ? { Authorization: `Bearer ${options.bearerToken}` } : {}),
      },
    }
  }

  private get acceptsFormat() {
    return this.options.acceptsFormat
  }

  /**
   * Creates a new ServiceClient instance with different configuration options while preserving
   * the original client's logger, service declaration, and transfer mechanism unchanged.
   *
   * This is useful when you need multiple client instances for the same service but with
   * different options objects. The original client remains unmodified and continues to operate
   * with its original configuration.
   *
   * @param options - New configuration options for the cloned client. These completely
   *                  replace the original client's options rather than merging with them.
   *
   * @returns A new ServiceClient instance with the specified options that operates
   *          independently from the original client.
   *
   * @example
   * ```typescript
   * // Create a base client for a service
   * const baseClient = new ServiceClient(logger, serviceDeclaration, transfer, {
   *   acceptsFormat: 'envelope',
   *   defaultHeaders: { 'X-Client-Version': '1.0' }
   * });
   *
   * // Clone with different authentication for a specific user
   * const userClient = baseClient.clone({
   *   acceptsFormat: 'envelope',
   *   bearerToken: 'user-jwt-token',
   *   defaultHeaders: { 'X-User-Context': 'user123' }
   * });
   *
   * // Clone with admin privileges and API key
   * const adminClient = baseClient.clone({
   *   acceptsFormat: 'envelope',
   *   apiKey: 'admin-api-key',
   *   defaultHeaders: { 'X-Admin-Context': 'admin-panel' }
   * });
   *
   * // All three clients can be used independently:
   * // - baseClient operates with original configuration
   * // - userClient operates with user authentication
   * // - adminClient operates with admin authentication
   * ```
   */
  clone(options: Options): ServiceClient<Service> {
    return new ServiceClient(this.logger, this.serviceDeclaration, this.transfer, {
      acceptsFormat: 'envelope',
      ...options,
    })
  }

  async callEndpoint<Endpoint extends EndpointOf<Service>>(
    endpoint: Endpoint,
    req: RequestOf<Service, Endpoint>,
    headers?: HeaderRecord,
    searchParams?: SearchParamsRecord,
  ): Promise<ResponseOf<Service, Endpoint>> {
    const dec = this.serviceDeclaration[endpoint]
    this.logger.info(`Calling endpoint "${endpoint}"`)

    const rawResponse = await this.doCallEndpoint(endpoint, dec, req, false, headers, searchParams)
    const response = this.doParseResponse(endpoint, dec, rawResponse)
    this.logger.debug(`Call to endpoint '${endpoint}' succeeded`)

    return response as ResponseOf<Service, Endpoint>
  }

  async callEndpointAsync<Endpoint extends EndpointOf<Service>>(
    endpoint: Endpoint,
    req: RequestOf<Service, Endpoint>,
    headers?: HeaderRecord,
    searchParams?: SearchParamsRecord,
  ): Promise<void> {
    const responselessClient = new ResponselessServiceClient(
      this.logger,
      this.serviceDeclaration,
      async (p: IncomingPayload) => {
        await this.transfer(p, { async: true })
      },
      this.acceptsFormat,
    )
    return responselessClient.callEndpointAsync(endpoint, req, headers, searchParams)
  }

  // TODO: reconsider this signature, passing endpoint declaration instances is a bid clumsy. Maybe a registry with all of them,
  // and then passing only the name of the endpoint (of a typed literal union).
  private async doCallEndpoint<Endpoint extends EndpointOf<Service>, Req, Res>(
    endpoint: Endpoint,
    { reqParser }: EndpointDeclaration<Req, Res>,
    request: Req,
    async: boolean,
    headers: HeaderRecord | undefined,
    searchParams: SearchParamsRecord | undefined,
  ): Promise<unknown> {
    // We intentionally do not use clock here, and get the actual time: we want to measure/log the real duration.
    const t0 = instantOf(Date.now())
    try {
      return await this.transfer(
        {
          endpointName: endpoint,
          acceptsFormat: this.acceptsFormat,
          endpointRequest: reqParser.parse(request),
          headers: emptyToUndefined({ ...this.options.defaultHeaders, ...headers }),
          searchParams,
        },
        { async },
      )
    } catch (error) {
      this.logger.error(`Error while calling endpoint '${endpoint}': ${error}`)
      return resolveError(error)
    } finally {
      const dt = instantOf(Date.now()).minus(t0)
      this.logger.info(`transfer call took ${dt.format()}`)
    }
  }

  private doParseResponse<Endpoint extends EndpointOf<Service>, Req, Res>(
    endpoint: Endpoint,
    { respParser }: EndpointDeclaration<Req, Res>,
    rawResponse: unknown,
  ): Res {
    try {
      const responseData = this.dataOrThrow(endpoint, rawResponse)
      const parsed = respParser.parse(responseData)
      return parsed
    } catch (error) {
      this.logger.error(`Call to endpoint '${endpoint}' errored: ${error}`)
      const resolvedError = resolveError(error)

      throw resolvedError
    }
  }

  private dataOrThrow(endpointName: string, rawResponse: unknown) {
    if (rawResponse instanceof Error) {
      // A non enveloped error response
      throw rawResponse
    }

    if (this.acceptsFormat === 'raw') {
      return rawResponse
    }

    if (this.acceptsFormat === 'envelope') {
      const envelope = ResponseEnvelope.parse(rawResponse)
      this.logger.info(
        `Response from "${endpointName}" came back with status "${envelope.status}" (traceId=${envelope.traceId})`,
      )
      if (envelope.status === 'error') {
        throw new EndpointCallError(envelope.error.code, { traceId: envelope.traceId, message: envelope.error.message })
      }
      return envelope.data
    }

    shouldNeverHappen(this.acceptsFormat)
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export class ResponselessServiceClient<Service extends ServiceDeclaration<any>> {
  private readonly options: Required<Options>
  constructor(
    private readonly logger: Logger,
    private readonly serviceDeclaration: Service,
    private readonly transfer: ResponselessTransfer,
    options: ResponseFormat | Options = {},
  ) {
    const resolvedOptions: Options = typeof options === 'string' ? { acceptsFormat: 'raw' } : options
    const defaultHeaders = { ...resolvedOptions.defaultHeaders }

    if (resolvedOptions.apiKey) {
      defaultHeaders['x-api-key'] = resolvedOptions.apiKey
    }

    if (resolvedOptions.bearerToken) {
      defaultHeaders['Authorization'] = `Bearer ${resolvedOptions.bearerToken}`
    }

    this.options = {
      acceptsFormat: 'raw',
      apiKey: '',
      bearerToken: '',
      ...resolvedOptions,
      defaultHeaders,
    }
  }

  async callEndpointAsync<Endpoint extends EndpointOf<Service>>(
    endpoint: Endpoint,
    req: RequestOf<Service, Endpoint>,
    headers?: HeaderRecord,
    searchParams?: SearchParamsRecord,
  ): Promise<void> {
    const dec = this.serviceDeclaration[endpoint]
    this.logger.debug(`Async calling endpoint '${endpoint}'`)

    await this.doCallEndpoint(endpoint, dec, req, headers, searchParams)
    this.logger.debug(`Async call to endpoint '${endpoint}' succeeded`)
  }

  // TODO(imaman): dedup (wrt ServiceClient)
  private async doCallEndpoint<Endpoint extends EndpointOf<Service>, Req, Res>(
    endpoint: Endpoint,
    { reqParser }: EndpointDeclaration<Req, Res>,
    request: Req,
    headers: HeaderRecord | undefined,
    searchParams: SearchParamsRecord | undefined,
  ): Promise<unknown> {
    // We intentionally do not use clock here, and get the actual time: we want to measure/log the real duration.
    const t0 = instantOf(Date.now())
    try {
      return await this.transfer({
        endpointName: endpoint,
        acceptsFormat: this.options.acceptsFormat,
        endpointRequest: reqParser.parse(request),
        headers: emptyToUndefined({ ...this.options.defaultHeaders, ...headers }),
        searchParams,
      })
    } catch (error) {
      this.logger.error(`Error while dispatching an async call to endpoint '${endpoint}': ${error}`)
      return resolveError(error)
    } finally {
      const dt = instantOf(Date.now()).minus(t0)
      this.logger.info(`dispatching an async call took ${dt.format()}`)
    }
  }
}

function resolveError(error: unknown) {
  if (error instanceof ZodError) {
    return new EndpointCallError(ERROR_CODE_PARSING, { message: error.message, cause: error })
  }

  if (error instanceof EndpointCallError) {
    return error
  }

  const message = errorLike(error, 'unknown error').message
  const traceId = error instanceof TransferError ? error.requestId : undefined
  return new EndpointCallError(ERROR_CODE_GENERIC, {
    message: `Could not get a response > ${message}`,
    traceId,
    cause: error,
  })
}

function emptyToUndefined<T extends object>(o: T) {
  return Object.keys(o).length > 0 ? o : undefined
}
